// ===========================
// Transfer Queue Status
// ===========================

export enum TransferStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  CALL_FAILED = 'call_failed',
  ARCHIVED = 'archived',
}

/** Display labels for status column */
export const TRANSFER_STATUS_LABEL: Record<TransferStatus, string> = {
  [TransferStatus.PENDING]: 'Pending',
  [TransferStatus.IN_PROGRESS]: 'In Progress',
  [TransferStatus.COMPLETED]: 'Completed',
  [TransferStatus.FAILED]: 'Failed',
  [TransferStatus.CANCELLED]: 'Cancelled',
  [TransferStatus.CALL_FAILED]: 'Call Failed',
  [TransferStatus.ARCHIVED]: 'Archived',
};

/** Normalize API queueStatus string to TransferStatus; falls back to PENDING or IN_PROGRESS from outcome if unknown */
export function toTransferStatus(record: { queueStatus?: string; outcome?: string }): TransferStatus {
  const s = record.queueStatus?.toLowerCase();
  if (s === TransferStatus.PENDING) return TransferStatus.PENDING;
  if (s === TransferStatus.IN_PROGRESS) return TransferStatus.IN_PROGRESS;
  if (s === TransferStatus.COMPLETED) return TransferStatus.COMPLETED;
  if (s === TransferStatus.FAILED) return TransferStatus.FAILED;
  if (s === TransferStatus.CANCELLED) return TransferStatus.CANCELLED;
  if (s === TransferStatus.CALL_FAILED) return TransferStatus.CALL_FAILED;
  if (s === TransferStatus.ARCHIVED) return TransferStatus.ARCHIVED;
  return record.outcome ? TransferStatus.IN_PROGRESS : TransferStatus.PENDING;
}

// ===========================
// Transfer Queue API Interfaces
// ===========================
// Matches GET /api/v1/transfer-queue response shape.

/**
 * Delivery window within a transfer queue record (e.g. "Evening (5pm - 9pm)").
 */
export interface TransferQueueWindow {
  start: number;
  end: number;
  label: string;
  /** Exact minutes from midnight; set on dispatcher-updated windows */
  startMinutes?: number;
  endMinutes?: number;
}

/**
 * Delivery date and time window for a transfer queue record.
 */
export interface TransferQueueDeliveryDateAndWindow {
  newDate: string;
  window: TransferQueueWindow;
}

/**
 * Single transfer queue record as returned by GET /transfer-queue.
 * Optional fields (outcome, comments, notes, updatedWindow, updatedDate) may be
 * present after create/update or from other endpoints.
 */
export interface TransferQueueRecord {
  _id: string;
  orderId: string;
  patientName: string;
  patientId: string;
  whiteGlove: boolean;
  city: string;
  postalCode: string;
  phone: string;
  rawPhone?: string;
  aiInferredPhone?: string;
  altPhone: string;
  rawAltPhone?: string;
  aiInferredAltPhone?: string;
  queueStatus: string;
  jobLocationId: string;
  deliveryDateAndWindow: TransferQueueDeliveryDateAndWindow;
  createdAt: string;
  updatedAt: string;
  outcome?: string;
  comments?: string;
  notes?: string;
  updatedWindow?: { start: number; end: number; label?: string; type?: string; startMinutes?: number; endMinutes?: number };
  updatedDate?: string;
}

/**
 * Pagination metadata returned by paginated endpoints.
 */
export interface TransferQueuePagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Response body for GET /transfer-queue.
 */
export interface TransferQueueApiResponse {
  success: boolean;
  message: string;
  data: TransferQueueRecord[];
  pagination: TransferQueuePagination;
  filtersContextual?: { cities?: string[]; locationCodes?: string[] };
}

// ===========================
// Create / Update request & response types
// ===========================

export interface CreateTransferQueueRequest {
  orderId: string;
  jobLocationId: string;
  clientId: string;
  outcome: string;
  needCallback?: boolean;
  transfer_reason?: string;
  cancellation_reason?: string;
  cancellation_type?: string;
  next_retry_at?: string;
  comments?: string;
  notes?: string;
  updatedWindow?: { start: number; end: number; label?: string; startMinutes?: number; endMinutes?: number };
  updatedDate?: string;
}

export interface UpdateTransferQueueRequest {
  outcome?: string;
  needCallback?: boolean;
  transfer_reason?: string;
  cancellation_reason?: string;
  cancellation_type?: string;
  next_retry_at?: string;
  comments?: string;
  notes?: string;
  updatedWindow?: { start: number; end: number; label?: string; startMinutes?: number; endMinutes?: number };
  updatedDate?: string;
}

export interface CreateTransferQueueResponse {
  success: boolean;
  message?: string;
  data: TransferQueueRecord;
  txId?: string;
}

export interface UpdateTransferQueueResponse {
  success: boolean;
  message?: string;
  data: TransferQueueRecord;
  txId?: string;
}

// ===========================
// Lock / Unlock response types
// ===========================

export interface LockTransferQueueResponse {
  success: boolean;
  message: string;
  data: {
    orderId: string;
    lockStatus?: {
      isBlocked: boolean;
      blockedBy: string;
      blockedAt: string;
      expiresAt: string;
    };
  };
}

export interface UnlockTransferQueueResponse {
  success: boolean;
  message: string;
  data: {
    orderId: string;
  };
}
